import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarRange } from 'lucide-react';
import { useTranslation } from '../../../i18n';

import { Card } from '../../../components/Shared/Card';
import { Loading } from '../../../components/Shared/Loading';
import { EmptyState } from '../../../components/Shared/EmptyState';
import { Badge } from '../../../components/Shared/Badge';
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow,
} from '../../../components/Shared/Table';

import { useInvoiceStore } from '../../../store/useInvoiceStore';
import { formatMoney } from '@/utils/money';
import { isStandingPlanValid, type StandingPlan } from '@/utils/standingPlan';
import { formatServiceMonths, paymentIntervalLabelKey } from '../../../types/billing';
import { BulkResultModal, type BulkResponse } from './BulkResultModal';
import { BillableRunFooter } from './BillableRunFooter';
import { NeedsReviewBadge } from './NeedsReviewBadge';
import { StandingInvoicePlanFields } from './StandingInvoicePlanFields';
import {
  ROW_CHECKBOX_CLASS,
  isBlocked,
  useBillableSelection,
  type BillableRow,
} from './billableRun';

// why: rows state one payment each (monthly, quarterly, …) — those do not add up; the footer
// sums the fixed amounts over each service period instead (FAFD-27).
const termNetOf = (row: BillableRow) => row.termNet ?? 0;

// why: typing a date fires a change per keystroke — wait until the input settles.
const RELOAD_DELAY_MS = 300;

interface StandingRunPanelProps {
  plan: StandingPlan;
  onPlanChange: (plan: StandingPlan) => void;
}

/**
 * Tab „Dauerrechnungen“: one standing invoice per object billed by standing invoice for the chosen
 * service period and payment interval, with a payment plan whose last payment per service year
 * takes the rounding difference (FACF-106, FAFD-27). Rows whose standing invoice already exists or
 * which the backend would reject are shown but cannot be selected.
 */
export function StandingRunPanel({ plan, onPlanChange }: StandingRunPanelProps) {
  const { t } = useTranslation(['invoices', 'common']);
  const navigate = useNavigate();

  const {
    standingBillable, loadingStandingBillable, fetchStandingBillable,
    bulkCreateStanding, saving,
  } = useInvoiceStore();

  const [bulkResult, setBulkResult] = useState<BulkResponse | null>(null);

  const planValid = isStandingPlanValid(plan);
  const planKey = `${plan.from}|${plan.to}|${plan.interval}`;

  const { from, to, interval } = plan;
  useEffect(() => {
    if (!planValid) return;
    const timer = window.setTimeout(() => {
      void fetchStandingBillable({ from, to, interval });
    }, RELOAD_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [from, to, interval, planValid, fetchStandingBillable]);

  const selection = useBillableSelection(standingBillable, planKey, termNetOf);

  const handleSubmit = async () => {
    if (selection.selectedIds.length === 0 || !planValid) return;
    try {
      const response = await bulkCreateStanding(plan, selection.selectedIds);
      if (response) {
        setBulkResult(response);
      }
    } catch {
      // showApiError already toasted; keep page state
    }
  };

  const closeBulkModal = () => {
    setBulkResult(null);
    void fetchStandingBillable(plan);
    selection.clear();
  };

  const openFirstCreated = () => {
    const firstSuccess = bulkResult?.results?.find((r) => r.invoiceId);
    if (firstSuccess?.invoiceId) {
      navigate(`/invoices/${firstSuccess.invoiceId}`);
    } else {
      navigate('/invoices');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Plan: service period + payment interval */}
      <div className="rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-3">
        <div className="max-w-2xl">
          <StandingInvoicePlanFields value={plan} onChange={onPlanChange} idPrefix="standing-run" />
        </div>
      </div>

      {!planValid ? (
        <EmptyState
          icon={<CalendarRange className="w-12 h-12" strokeWidth={1.75} />}
          title={t('invoices:standingPlan.invalidTitle')}
          description={t('invoices:standingPlan.invalidHint')}
        />
      ) : loadingStandingBillable ? (
        <Loading />
      ) : standingBillable.length === 0 ? (
        <EmptyState
          icon={<CalendarRange className="w-12 h-12" strokeWidth={1.75} />}
          title={t('invoices:standing.empty')}
          description={t('invoices:standing.emptyHint')}
        />
      ) : (
        <>
          <Card noPadding>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader className="w-10">
                    <input
                      type="checkbox"
                      checked={selection.allSelectableSelected}
                      onChange={selection.toggleAll}
                      disabled={selection.selectable.length === 0}
                      aria-label={t('invoices:billable.selectAll')}
                      className={ROW_CHECKBOX_CLASS}
                    />
                  </TableHeader>
                  <TableHeader>{t('invoices:fields.customer')}</TableHeader>
                  <TableHeader>{t('invoices:fields.object')}</TableHeader>
                  <TableHeader className="hidden md:table-cell">
                    {t('invoices:billable.offerNumber')}
                  </TableHeader>
                  <TableHeader>{t('invoices:standing.period')}</TableHeader>
                  <TableHeader className="text-right">
                    {t('invoices:standing.periodNet')}
                  </TableHeader>
                  <TableHeader className="hidden lg:table-cell text-right">
                    {t('invoices:standing.termNet')}
                  </TableHeader>
                  <TableHeader>{t('invoices:standing.status')}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {standingBillable.map((b) => {
                  const isInvoiced = b.alreadyInvoiced === true;
                  return (
                    <TableRow key={b.objectId} className={isInvoiced ? 'opacity-50' : ''}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selection.isSelected(b.objectId)}
                          disabled={isInvoiced || isBlocked(b)}
                          onChange={() => selection.toggleOne(b.objectId)}
                          aria-label={b.objectName || ''}
                          className={ROW_CHECKBOX_CLASS}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="text-[13px] font-medium text-slate-900">
                          {b.customerName || '—'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13px] text-slate-700">{b.objectName || '—'}</span>
                          <NeedsReviewBadge row={b} />
                          {b.overlapsBilledMonths && (
                            <Badge variant="warning" title={t('invoices:standing.overlapsBilledMonthsHint')}>
                              {t('invoices:standing.overlapsBilledMonths')}
                            </Badge>
                          )}
                          {b.startsBeforeOffer && (
                            <Badge variant="warning" title={t('invoices:standing.startsBeforeOfferHint')}>
                              {t('invoices:standing.startsBeforeOffer')}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-[13px] text-slate-600 font-mono-num">
                        {b.offerNumber || '—'}
                      </TableCell>
                      <TableCell className="text-[13px] text-slate-700 tabular-nums whitespace-nowrap">
                        {formatServiceMonths(b.serviceDateFrom, b.serviceDateTo) ?? '—'}
                      </TableCell>
                      <TableCell className="text-right text-[13px] font-medium text-slate-900 tabular-nums">
                        {formatMoney(b.totalNet)}
                        <div className="text-[11px] font-normal text-slate-500">
                          {t(paymentIntervalLabelKey(b.paymentInterval))}
                        </div>
                        {b.adjustedPaymentNet != null && (
                          <div className="text-[11px] font-normal text-slate-500" title={t('invoices:standingPlan.adjustedHint')}>
                            {t('invoices:standing.adjustedPayment', { amount: formatMoney(b.adjustedPaymentNet) })}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-right text-[13px] text-slate-700 tabular-nums">
                        {formatMoney(b.termNet)}
                      </TableCell>
                      <TableCell>
                        {isInvoiced ? (
                          <Badge variant="warning">{t('invoices:standing.alreadyCreated')}</Badge>
                        ) : (
                          <Badge variant="default">{t('invoices:standing.open')}</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          <BillableRunFooter
            summary={t('invoices:standing.selectedSummary', {
              selected: selection.selectedIds.length,
              total: selection.selectable.length,
              amount: formatMoney(selection.selectedTotal),
            })}
            submitLabel={t('invoices:standing.bulkCreate')}
            disabled={selection.selectedIds.length === 0}
            saving={saving}
            onSubmit={handleSubmit}
          />
        </>
      )}

      {bulkResult && (
        <BulkResultModal
          response={bulkResult}
          rows={standingBillable}
          onClose={closeBulkModal}
          onOpenCreated={openFirstCreated}
        />
      )}
    </div>
  );
}
